import {useAccessToken, useIsUnitComplianceUser, useIsUnitUser} from "../../services/auth"
import React, {useEffect, useState} from "react"
import TitleBar, {TitleBarAction} from "../../components/TitleBar/TitleBar"
import {MainSection} from "../../containers/MainSection/MainSection"
import HorizontalField, {optionalHorizontalField} from "../../components/HorizontalField/HorizontalField"
import {
	BusinessCustomer,
	CustomerStatus,
	CustomerType,
	CustomerTypeIcons,
	UpdateBusinessCustomerAttributes,
	updateBusinessCustomer,
	updateCustomerTags,
} from "../../resources/customer"
import Card from "../../containers/Card/Card"
import {emailPreview} from "../../components/InformationHiding/InformationHiding"
import TagsCard from "../../components/Tags/TagsCard"
import {Transactions} from "../../components/Transactions/Transactions"
import {TransactionsColumns} from "../../components/Transactions/Transactions.types"
import {UnitOrOrgManageResubmitOrBankOnly, UnitUserOnly} from "../../containers/PermissionedUser/PermissionedUser"
import {createEmptyAddress, ErrorDocument, OkDocument} from "../../resources/common"
import {EditableRiskRateField, RiskRateField} from "../../components/RiskRateField/RiskRateField"
import {useToasts} from "react-toast-notifications"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {
	Editable,
	EditableAddress,
	EditableFullName,
	EditableHiddenString,
	EditableOptionalString,
	EditablePhone,
	EditableString,
	OptionalEditable,
} from "../../components/HorizontalField/EditableHorizontalField"
import {isUndefined, startCase} from "lodash"
import {stateRegex} from "../../utilities/validation"
import {booleanToYesNo} from "../../utilities/helpers"
import {AuthorizedUsers} from "../CustomerAuthorizedUsers/CustomerAuthorizedUsers"
import {useCanAccessProductApplications, hasPermission} from "../../services/permission"
import CustomerKnownNamesComponent from "../../components/CustomerKnownNames/CustomerKnownNames"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {CustomerKnownNames, getCustomerKnownNames} from "../../resources/customerKnownNames"
import CustomerCounterparties from "../../components/CustomerCounterParties/CustomerCounterparties"
import {HasPermission} from "../../containers/PermissionedUser/Permission"
import {useModal} from "react-modal-hook"
import {ArchiveCustomerModal} from "../Customer/ArchiveCustomerModal"
import {UnarchiveCustomerModal} from "../Customer/UnarchiveCustomerModal"
import {Disputes} from "../../components/Disputes/Disputes"
import {DisputesColumns} from "../../components/Disputes/Disputes.types"
import {Accounts} from "../../components/Accounts/Accounts"
import {ActionButton, ActionItem} from "../../components/ActionButton/ActionButton"
import {isOrg, Org} from "../../resources/org"
import {BusinessAdditionalInformationFields} from "../../components/BusinessAdditionalInformationFields/BusinessAdditionalInformationFields"
import {SpecificCustomerProps} from "../Customer/Customer"
import {useAuditLog} from "../../components/AuditLogDrawer/useAuditLog"
import {getAuditLogEventsHeaderMetaItems} from "../Customer/auditLogMetaItems"
import AuditLogActionButton from "../../components/AuditLogActionButton/AuditLogActionButton"
import AuditLogDrawer from "../../components/AuditLogDrawer/AuditLogDrawer"
import {OneProgramAdmonition} from "../../components/OneProgranAdmonition/OneProgramAdmonition"
import EvaluationType from "../../components/ApplicationDecision/EvaluationType"
import ReceivedPayments, {ReceivedPaymentsColumns} from "../../components/ReceivedPayments/ReceivedPayments"
import {AccountsColumns} from "../../components/Accounts/Accounts.types"
import {ProductApplications} from "../../components/ProductApplications/ProductApplications"
import {ProductApplicationsColumns} from "../../resources/productApplication"
import {useListReturnPath} from "../../hooks/useListReturnPath"
import Bills, {BillsColumns} from "../../components/Bills/Bills"
import {getDocumentTitle} from "../../components/IdentificationNumber/IdentificationNumber"
import {isCustomerOfficer} from "../../resources/customerOfficer"
import {isCustomerBeneficialOwner} from "../../resources/customerBeneficialOwner"
import moment from "moment"
import CustomerBeneficialOwners from "../../components/BeneficialOwners/CustomerBeneficialOwners"
import {CustomerOfficer} from "../../components/Officer/CustomerOfficer"
import {EditableBusinessEntityTypeField} from "../../components/BusinessEntityTypeField/BusinessEntityTypeField"

export default function BusinessPage({value: customer, refresh}: SpecificCustomerProps<BusinessCustomer>) {
	const listReturnPath = useListReturnPath("/customers/")
	const accessToken = useAccessToken()
	const isUnitUser = useIsUnitUser()
	const {addToast} = useToasts()
	const canAccessProductApplications = useCanAccessProductApplications()
	const business = customer.data
	const {attributes} = business
	const {contact} = attributes
	const org = customer.included?.find((r) => isOrg(r) && r.id == business.relationships.org.data.id) as unknown as Org
	const officerResources = customer.included?.filter(isCustomerOfficer) || []
	const officerAttributes = officerResources.length > 0 ? officerResources[0].attributes : undefined
	const beneficialOwnersResources = customer.included?.filter(isCustomerBeneficialOwner) || []
	const [isEditing, setIsEditing] = useState<string | undefined>(undefined)
	const [isUpdating, setIsUpdating] = useState(false)
	const [address, setAddress] = useState(attributes.address)
	const [operatingAddress, setOperatingAddress] = useState(attributes.operatingAddress)
	const [phone, setPhone] = useState(attributes.phone)
	const [ein, setEin] = useState(attributes.ein)
	const [dba, setDba] = useState(attributes.dba)
	const [entityType, setEntityType] = useState(attributes.entityType)
	const [stateOfIncorporation, setStateOfIncorporation] = useState(attributes.stateOfIncorporation)
	const [riskRate, setRiskRate] = useState(attributes.riskRate)
	const [overrideRiskRate, setOverrideRiskRate] = useState(attributes.overrideRiskRate)
	const [contactName, setContactName] = useState(contact.fullName)
	const [contactEmail, setContactEmail] = useState(contact.email)
	const [contactPhone, setContactPhone] = useState(contact.phone)
	const [name, setName] = useState(attributes.name)
	const [officerName, setOfficerName] = useState(officerAttributes?.fullName)
	const [officerTitle, setOfficerTitle] = useState(officerAttributes?.title)
	const [officerDob, setOfficerDob] = useState(
		officerAttributes?.dateOfBirth ? moment(officerAttributes.dateOfBirth).toDate() : undefined
	)
	const [officerAddress, setOfficerAddress] = useState(officerAttributes?.address)
	const [officerPhone, setOfficerPhone] = useState(officerAttributes?.phone)
	const [officerEmail, setOfficerEmail] = useState(officerAttributes?.email)
	const [officerOccupation, setOfficerOccupation] = useState(officerAttributes?.occupation)
	const [officerAnnualIncome, setOfficerAnnualIncome] = useState(officerAttributes?.annualIncome)
	const [officerSourceOfIncome, setOfficerSourceOfIncome] = useState(officerAttributes?.sourceOfIncome)
	const [stockSymbol, setStockSymbol] = useState(attributes.stockSymbol)
	const [businessVertical, setBusinessVertical] = useState(attributes.businessVertical)
	const [website, setWebsite] = useState(attributes.website)
	const [numberOfEmployees, setNumberOfEmployees] = useState(attributes.numberOfEmployees)
	const [yearOfIncorporation, setYearOfIncorporation] = useState(attributes.yearOfIncorporation)
	const [annualRevenue, setAnnualRevenue] = useState(attributes.annualRevenue)
	const [cashFlow, setCashFlow] = useState(attributes.cashFlow)
	const [countriesOfOperation, setCountriesOfOperation] = useState(attributes.countriesOfOperation)
	const [businessIndustry, setBusinessIndustry] = useState(attributes.businessIndustry)
	const [sourceOfFunds, setSourceOfFunds] = useState(attributes.sourceOfFunds)
	const [sourceOfFundsDescription, setSourceOfFundsDescription] = useState(attributes.sourceOfFundsDescription)
	const [accountPurpose, setAccountPurpose] = useState(attributes.accountPurpose)
	const [accountPurposeDetail, setAccountPurposeDetail] = useState(attributes.accountPurposeDetail)
	const [transactionVolume, setTransactionVolume] = useState(attributes.transactionVolume)
	const [transactionVolumeDescription, setTransactionVolumeDescription] = useState(
		attributes.transactionVolumeDescription
	)
	const [isRegulated, setIsRegulated] = useState(attributes.isRegulated)
	const [regulatorName, setRegulatorName] = useState(attributes.regulatorName)
	const [usNexus, setUsNexus] = useState(attributes.usNexus)
	const [businessDescription, setBusinessDescription] = useState(attributes.businessDescription)
	const [updateState, updateApp] = useAsyncResultIdle(updateBusinessCustomer)
	const [refreshToken] = useRefreshToken()
	const isArchived = attributes.status == CustomerStatus.Archived
	const isCustomerEditable = !isArchived
	const isHiddenIconVisible = !useIsUnitComplianceUser()
	const [showArchiveCustomer, hideArchiveCustomer] = useModal(() => (
		<ArchiveCustomerModal closeModal={hideArchiveCustomer} customer={business} onSuccess={refresh} />
	))
	const [showUnarchiveCustomer, hideUnarchiveCustomer] = useModal(() => (
		<UnarchiveCustomerModal closeModal={hideUnarchiveCustomer} customer={business} onSuccess={refresh} />
	))
	const customerKnownNamesResult = isUnitUser
		? useAsyncResult<OkDocument<CustomerKnownNames>, ErrorDocument>(
				() => getCustomerKnownNames(accessToken, business.id),
				[refreshToken]
		  )
		: null
	const applicationLink = business.relationships.application
		? `/applications/business/${business.relationships.application.data.id}`
		: undefined

	const officerDocumentTitle = getDocumentTitle(
		officerAttributes?.ssn,
		officerAttributes?.maskedSSN,
		officerAttributes?.passport,
		officerAttributes?.maskedPassport,
		officerAttributes?.matriculaConsular,
		officerAttributes?.maskedMatriculaConsular
	)

	useEffect(() => {
		if (updateState.isOk()) {
			addToast("Customer Updated Successfully", {appearance: "success"})
			setIsUpdating(false)
			refresh()
		} else if (updateState.isErr()) {
			addToast(updateState.error.errors[0].detail || updateState.error.errors[0].title, {appearance: "error"})
			setIsUpdating(false)
			refresh()
		}
	}, [updateState])

	const dbaComponent = (
		<Editable
			label="Doing Business As"
			isEditing={isEditing === "DBA"}
			isDisabled={!isUndefined(isEditing)}
			isAllowed={isCustomerEditable && hasPermission("customer.dba", "update")}
			isUpdating={isUpdating}
			onStartEdit={() => setIsEditing("DBA")}
			onCancel={refresh}
			setValue={setDba}
			value={dba}
			initial={dba}
			update={() => {
				updateApp(accessToken, business, {dba: dba === "" ? null : dba})
				setIsUpdating(true)
			}}
			pattern={"^.*$"}
			className={isArchived ? "archived-field" : ""}
			required={false}
		>
			{EditableOptionalString}
		</Editable>
	)

	const customerType = CustomerType.business
	const customerTypeDisplayText = startCase(customerType)
	const customerTypeIcon = CustomerTypeIcons[customerType]
	const updateFunction = (attributes: UpdateBusinessCustomerAttributes) => updateApp(accessToken, business, attributes)
	const auditLogEventsHeaderMetaItems = getAuditLogEventsHeaderMetaItems({
		customerName: name,
		customerType: customerTypeDisplayText,
		pageIcon: customerTypeIcon,
		org: org as Org,
	})
	const {showAuditLog, openAuditLogDrawer, closeAuditLogDrawer, isAuditLogDrawerOpened} = useAuditLog()
	return (
		<>
			<TitleBar
				title={name}
				breadcrumbs={[{text: "Customers", path: listReturnPath}]}
				meta={[
					{text: customerTypeDisplayText, icon: customerTypeIcon},
					{text: "Application", icon: "user-id-card", path: applicationLink},
				]}
				status={isArchived ? {text: "Archived", color: "#B0B2B5", backgroundColor: "#EBECEE"} : undefined}
			>
				{showAuditLog && <AuditLogActionButton onClick={openAuditLogDrawer} />}
				{attributes.status != CustomerStatus.Archived ? (
					<HasPermission resource="customer.status" action="update">
						<TitleBarAction>
							<ActionButton enableActions={true}>
								<ActionItem
									title={"Archive customer"}
									color={"#FF506F"}
									icon={"user-geometric-action-cross-remove"}
									onClick={showArchiveCustomer}
								/>
							</ActionButton>
						</TitleBarAction>
					</HasPermission>
				) : (
					<HasPermission resource="customer.status.unarchive" action="update">
						<TitleBarAction>
							<ActionButton enableActions={true}>
								<ActionItem
									title={"Unarchive customer"}
									color={"#FF506F"}
									icon={"user-geometric-action-cross-remove"}
									onClick={showUnarchiveCustomer}
								/>
							</ActionButton>
						</TitleBarAction>
					</HasPermission>
				)}
			</TitleBar>
			{org?.relationships?.parentOrg && <OneProgramAdmonition />}

			<MainSection>
				<AuditLogDrawer
					open={isAuditLogDrawerOpened}
					onClose={closeAuditLogDrawer}
					auditLogEventsHeaderMetaItems={auditLogEventsHeaderMetaItems}
					resourceType="customer"
					showSubResourcesId={true}
					resourceId={business.id}
				/>
				<div className="columns">
					<div className="column is-4">
						<div className="columns">
							<div className="column">
								<div className="card">
									<div className="card-header">
										<p className="card-header-title">Contact</p>
									</div>
									<div className="card-content">
										<UnitUserOnly>
											{optionalHorizontalField(
												"Org",
												org && org.type == "org" ? org.attributes.name : undefined,
												undefined,
												isArchived ? "archived-field" : ""
											)}
										</UnitUserOnly>
										<Editable
											label="Name"
											isEditing={isEditing === "Contact Name"}
											isDisabled={!isUndefined(isEditing)}
											isAllowed={isCustomerEditable && hasPermission("customer", "update")}
											isUpdating={isUpdating}
											onStartEdit={() => setIsEditing("Contact Name")}
											onCancel={refresh}
											setValue={setContactName}
											value={contactName}
											initial={contactName}
											update={() => {
												updateApp(accessToken, business, {contactName: contactName})
												setIsUpdating(true)
											}}
											className={isArchived ? "archived-field" : ""}
										>
											{EditableFullName}
										</Editable>
										<OptionalEditable
											label="Phone"
											isEditing={isEditing === "Contact Phone"}
											isDisabled={!isUndefined(isEditing)}
											isAllowed={isCustomerEditable && hasPermission("customer", "update")}
											isUpdating={isUpdating}
											isHiddenIconVisible={isHiddenIconVisible}
											onStartEdit={() => setIsEditing("Contact Phone")}
											onCancel={refresh}
											setValue={setContactPhone}
											value={contactPhone}
											initial={contactPhone}
											update={() => {
												updateApp(accessToken, business, {contactPhone: contactPhone})
												setIsUpdating(true)
											}}
											className={isArchived ? "archived-field" : ""}
										>
											{EditablePhone}
										</OptionalEditable>
										<OptionalEditable
											label="Email"
											isEditing={isEditing === "Contact Email"}
											isDisabled={!isUndefined(isEditing)}
											isAllowed={isCustomerEditable && hasPermission("customer", "update")}
											isUpdating={isUpdating}
											isHiddenIconVisible={isHiddenIconVisible}
											onStartEdit={() => setIsEditing("Contact Email")}
											onCancel={refresh}
											setValue={setContactEmail}
											value={contactEmail}
											initial={contactEmail}
											update={() => {
												updateApp(accessToken, business, {contactEmail: contactEmail})
												setIsUpdating(true)
											}}
											inputType="email"
											previewFunction={emailPreview}
											className={isArchived ? "archived-field" : ""}
										>
											{EditableHiddenString}
										</OptionalEditable>
									</div>
								</div>
							</div>
						</div>
						<div className="columns">
							<div className="column">
								<div className="card">
									<div className="card-header">
										<p className="card-header-title">Business Information</p>
									</div>
									<div className="card-content">
										<HorizontalField label="Customer Id" fieldClassName={isArchived ? "archived-field" : ""}>
											<input type="text" className="input is-static" readOnly value={business.id} />
										</HorizontalField>
										<Editable
											label="Name"
											isEditing={isEditing === "Name"}
											isDisabled={!isUndefined(isEditing)}
											isAllowed={isCustomerEditable && isUnitUser}
											isUpdating={isUpdating}
											onStartEdit={() => setIsEditing("Name")}
											onCancel={refresh}
											setValue={setName}
											value={name}
											initial={name}
											update={() => {
												updateApp(accessToken, business, {name: name})
												setIsUpdating(true)
											}}
											className={isArchived ? "archived-field" : ""}
										>
											{EditableString}
										</Editable>
										<UnitUserOnly>
											{overrideRiskRate ? (
												<>
													<RiskRateField riskRate={riskRate} />
													<EditableRiskRateField
														label="Override Risk Rate"
														riskRate={overrideRiskRate}
														setRiskRate={setOverrideRiskRate}
														isEditing={isEditing === "RiskRate"}
														isDisabled={!isUndefined(isEditing)}
														isAllowed={isCustomerEditable && isUnitUser}
														onStartEdit={() => setIsEditing("RiskRate")}
														onCancel={refresh}
														update={() => {
															updateApp(accessToken, business, {riskRate: overrideRiskRate})
															setIsUpdating(true)
														}}
														className={isArchived ? "archived-field" : ""}
													/>
												</>
											) : (
												riskRate && (
													<EditableRiskRateField
														riskRate={riskRate}
														setRiskRate={setRiskRate}
														isEditing={isEditing === "RiskRate"}
														isDisabled={!isUndefined(isEditing)}
														isAllowed={isCustomerEditable && isUnitUser}
														onStartEdit={() => setIsEditing("RiskRate")}
														onCancel={refresh}
														update={() => {
															updateApp(accessToken, business, {riskRate: riskRate})
															setIsUpdating(true)
														}}
														className={isArchived ? "archived-field" : ""}
													/>
												)
											)}
										</UnitUserOnly>
										<UnitOrOrgManageResubmitOrBankOnly>
											{business.attributes.externalCip === true ? <EvaluationType /> : null}
										</UnitOrOrgManageResubmitOrBankOnly>
										{dbaComponent}
										<EditableBusinessEntityTypeField
											entityType={entityType}
											setEntityType={setEntityType}
											isEditing={isEditing === "Entity Type"}
											isDisabled={!isUndefined(isEditing)}
											isAllowed={isCustomerEditable && hasPermission("customer.entityType", "update")}
											isUpdating={isUpdating}
											onStartEdit={() => setIsEditing("Entity Type")}
											onCancel={refresh}
											update={() => {
												updateApp(accessToken, business, {entityType})
												setIsUpdating(true)
											}}
											className={isArchived ? "archived-field" : ""}
										/>
										<OptionalEditable
											label="EIN"
											isEditing={isEditing === "EIN"}
											isDisabled={!isUndefined(isEditing)}
											isAllowed={isCustomerEditable && isUnitUser}
											isUpdating={isUpdating}
											isHiddenIconVisible={isHiddenIconVisible}
											onStartEdit={() => setIsEditing("EIN")}
											onCancel={refresh}
											setValue={setEin}
											value={ein}
											initial={ein}
											update={() => {
												updateApp(accessToken, business, {ein: ein})
												setIsUpdating(true)
											}}
											pattern="^\d{9}$"
											className={isArchived ? "archived-field" : ""}
										>
											{EditableHiddenString}
										</OptionalEditable>
										<Editable
											label="Incorporation State"
											isEditing={isEditing === "Incorporation State"}
											isDisabled={!isUndefined(isEditing)}
											isAllowed={isCustomerEditable && isUnitUser}
											isUpdating={isUpdating}
											onStartEdit={() => setIsEditing("Incorporation State")}
											onCancel={refresh}
											setValue={setStateOfIncorporation}
											value={stateOfIncorporation}
											initial={stateOfIncorporation}
											update={() => {
												updateApp(accessToken, business, {stateOfIncorporation: stateOfIncorporation})
												setIsUpdating(true)
											}}
											pattern={stateRegex}
											className={isArchived ? "archived-field" : ""}
										>
											{EditableString}
										</Editable>
										<OptionalEditable
											label="Phone"
											isEditing={isEditing === "Phone"}
											isDisabled={!isUndefined(isEditing)}
											isAllowed={isCustomerEditable && hasPermission("customer.phone", "update")}
											isUpdating={isUpdating}
											isHiddenIconVisible={isHiddenIconVisible}
											onStartEdit={() => setIsEditing("Phone")}
											onCancel={refresh}
											setValue={setPhone}
											value={phone}
											initial={phone}
											update={() => {
												updateApp(accessToken, business, {phone: phone})
												setIsUpdating(true)
											}}
											className={isArchived ? "archived-field" : ""}
										>
											{EditablePhone}
										</OptionalEditable>
										<OptionalEditable
											label="Address"
											isEditing={isEditing === "Address"}
											isDisabled={!isUndefined(isEditing)}
											isAllowed={isCustomerEditable && hasPermission("customer.address", "update")}
											isUpdating={isUpdating}
											onStartEdit={() => setIsEditing("Address")}
											onCancel={refresh}
											setValue={setAddress}
											value={address}
											initial={address}
											update={(v) => {
												updateApp(accessToken, business, {address: v})
												setIsUpdating(true)
											}}
											className={isArchived ? "archived-field" : ""}
										>
											{EditableAddress}
										</OptionalEditable>
										<OptionalEditable
											label="Operating Address"
											isEditing={isEditing === "OperatingAddress"}
											isDisabled={!isUndefined(isEditing)}
											isAllowed={isCustomerEditable && hasPermission("customer.address", "update")}
											isUpdating={isUpdating}
											onStartEdit={() => setIsEditing("OperatingAddress")}
											onCancel={refresh}
											setValue={setOperatingAddress}
											value={operatingAddress ?? createEmptyAddress()}
											initial={operatingAddress ?? createEmptyAddress()}
											update={() => {
												updateApp(accessToken, business, {operatingAddress: operatingAddress})
												setIsUpdating(true)
											}}
											className={isArchived ? "archived-field" : ""}
										>
											{EditableAddress}
										</OptionalEditable>
										{optionalHorizontalField(
											"Attested no beneficial owners",
											booleanToYesNo(attributes.attestedNoBeneficialOwners),
											undefined,
											isArchived ? "archived-field" : ""
										)}
										{optionalHorizontalField(
											"Archive Reason",
											startCase(attributes.archiveReason),
											undefined,
											isArchived ? "archived-field" : ""
										)}

										<BusinessAdditionalInformationFields
											customer={business}
											beneficialOwners={beneficialOwnersResources}
											stockSymbol={stockSymbol}
											setStockSymbol={setStockSymbol}
											businessVertical={businessVertical}
											setBusinessVertical={setBusinessVertical}
											website={website}
											setWebsite={setWebsite}
											numberOfEmployees={numberOfEmployees}
											setNumberOfEmployees={setNumberOfEmployees}
											yearOfIncorporation={yearOfIncorporation}
											setYearOfIncorporation={setYearOfIncorporation}
											annualRevenue={annualRevenue}
											setAnnualRevenue={setAnnualRevenue}
											cashFlow={cashFlow}
											setCashFlow={setCashFlow}
											countriesOfOperation={countriesOfOperation}
											setCountriesOfOperation={setCountriesOfOperation}
											businessIndustry={businessIndustry}
											setBusinessIndustry={setBusinessIndustry}
											sourceOfFunds={sourceOfFunds}
											setSourceOfFunds={setSourceOfFunds}
											sourceOfFundsDescription={sourceOfFundsDescription}
											setSourceOfFundsDescription={setSourceOfFundsDescription}
											accountPurpose={accountPurpose}
											setAccountPurpose={setAccountPurpose}
											accountPurposeDetail={accountPurposeDetail}
											setAccountPurposeDetail={setAccountPurposeDetail}
											transactionVolume={transactionVolume}
											setTransactionVolume={setTransactionVolume}
											transactionVolumeDescription={transactionVolumeDescription}
											setTransactionVolumeDescription={setTransactionVolumeDescription}
											isRegulated={isRegulated}
											setIsRegulated={setIsRegulated}
											regulatorName={regulatorName}
											setRegulatorName={setRegulatorName}
											usNexus={usNexus}
											setUsNexus={setUsNexus}
											businessDescription={businessDescription}
											setBusinessDescription={setBusinessDescription}
											isEditing={isEditing}
											setIsEditing={setIsEditing}
											setIsUpdating={setIsUpdating}
											isAllowed={isCustomerEditable && hasPermission("customer", "update")}
											updateFunction={updateFunction}
											refresh={refresh}
										/>
									</div>
								</div>
							</div>
						</div>

						<Card title="Counterparties">
							<CustomerCounterparties customerId={business.id} limit={100} isArchived={isArchived} />
						</Card>

						<div className="columns">
							<div className="column">
								<TagsCard
									tags={attributes.tags}
									onUpdateTags={(token, tags) => updateCustomerTags(token, business.id, business.type, tags)}
									refresh={refresh}
									editable={hasPermission("customer", "update")}
								/>
							</div>
						</div>
						{officerAttributes && (
							<div className="columns">
								<div className="column">
									<CustomerOfficer
										officerAttributes={officerAttributes}
										customerId={business.id}
										officerId={officerResources[0].id}
										isAllowed={isCustomerEditable && hasPermission("customer", "update")}
										officerDocumentTitle={officerDocumentTitle}
										officerName={officerName}
										setOfficerName={setOfficerName}
										officerTitle={officerTitle}
										setOfficerTitle={setOfficerTitle}
										officerDob={officerDob}
										setOfficerDob={setOfficerDob}
										officerPhone={officerPhone}
										setOfficerPhone={setOfficerPhone}
										officerEmail={officerEmail}
										setOfficerEmail={setOfficerEmail}
										officerAddress={officerAddress}
										setOfficerAddress={setOfficerAddress}
										officerOccupation={officerOccupation}
										setOfficerOccupation={setOfficerOccupation}
										officerAnnualIncome={officerAnnualIncome}
										setOfficerAnnualIncome={setOfficerAnnualIncome}
										officerSourceOfIncome={officerSourceOfIncome}
										setOfficerSourceOfIncome={setOfficerSourceOfIncome}
										isEditing={isEditing}
										setIsEditing={setIsEditing}
										isUpdating={isUpdating}
										setIsUpdating={setIsUpdating}
										isHiddenIconVisible={isHiddenIconVisible}
										refresh={refresh}
									/>
								</div>
							</div>
						)}
						{customerKnownNamesResult?.match(
							() => null,
							(aliases) => (
								<div className="columns">
									<div className="column">
										<div className="card">
											<div className="card-header">
												<p className="card-header-title">Known Aliases</p>
											</div>
											<div className="card-content">
												<CustomerKnownNamesComponent
													customerId={business.id}
													names={aliases.data.attributes.names}
													refresh={refresh}
													fieldClassName={"archived-field"}
												/>
											</div>
										</div>
									</div>
								</div>
							),
							() => null
						)}
					</div>
					<div className="column">
						<Accounts
							customerId={business.id}
							enableTitle
							fullHeight={false}
							includedColumns={[
								AccountsColumns.id,
								...(isUnitUser ? [AccountsColumns.bank] : []),
								AccountsColumns.balance,
								AccountsColumns.type,
								AccountsColumns.product,
								AccountsColumns.status,
								AccountsColumns.routingNumber,
								AccountsColumns.accountNumber,
								AccountsColumns.purpose,
								AccountsColumns.createdAt,
							]}
						/>

						<Transactions
							fullHeight={false}
							customerId={business.id}
							limit={10}
							isUsingPaging={true}
							includedColumns={[
								TransactionsColumns.id,
								TransactionsColumns.type,
								TransactionsColumns.amount,
								TransactionsColumns.balance,
								TransactionsColumns.summary,
								TransactionsColumns.createdAt,
							]}
						/>

						<ReceivedPayments
							fullHeight={false}
							enableTitle={true}
							enableSearchBox={false}
							disableDrawer={true}
							customerId={business.id}
							includedColumns={[
								ReceivedPaymentsColumns.id,
								ReceivedPaymentsColumns.type,
								ReceivedPaymentsColumns.direction,
								ReceivedPaymentsColumns.companyName,
								ReceivedPaymentsColumns.amount,
								ReceivedPaymentsColumns.status,
								ReceivedPaymentsColumns.createdAt,
								ReceivedPaymentsColumns.completionDate,
							]}
						/>

						<Disputes
							enableTitle={true}
							fullHeight={false}
							limit={10}
							customerId={business.id}
							includedColumns={[
								DisputesColumns.id,
								DisputesColumns.source,
								DisputesColumns.amount,
								DisputesColumns.createdAt,
								DisputesColumns.status,
								DisputesColumns.updatedAt,
							]}
						/>

						<AuthorizedUsers customerId={business.id} />

						<CustomerBeneficialOwners
							customerId={business.id}
							beneficialOwners={beneficialOwnersResources}
							refresh={refresh}
						/>

						{canAccessProductApplications && (
							<ProductApplications
								customerId={business.id}
								enableTitle={true}
								fullHeight={false}
								limit={10}
								refreshToken={refreshToken}
								includedColumns={[
									ProductApplicationsColumns.id,
									ProductApplicationsColumns.type,
									ProductApplicationsColumns.name,
									ProductApplicationsColumns.status,
									ProductApplicationsColumns.createdAt,
								]}
							/>
						)}

						{hasPermission("bill", "get") && (
							<Bills
								customerId={business.id}
								enableTitle={true}
								fullHeight={false}
								limit={10}
								enableStatusFilter={true}
								includedColumns={[
									BillsColumns.id,
									BillsColumns.invoiceNumber,
									BillsColumns.vendor,
									BillsColumns.amount,
									BillsColumns.status,
									BillsColumns.dueDate,
									BillsColumns.createdAt,
								]}
							/>
						)}
					</div>
				</div>
			</MainSection>
		</>
	)
}
